/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react"
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from "@mui/material"
import { collection, onSnapshot, QuerySnapshot } from "firebase/firestore"
import { db } from "../../firebase"
import { tokens } from "../../theme"
import { useNavigate } from "react-router-dom"
import AddIcon from "@mui/icons-material/Add"
import dayjs from "dayjs"
import { IBusinessHoliday, isRangeFullyHoliday } from "../../utils/businessHolidays"

const MONTHS = [
  { value: "04", label: "４月" },
  { value: "05", label: "５月" },
  { value: "06", label: "６月" },
  { value: "07", label: "７月" },
  { value: "08", label: "８月" },
  { value: "09", label: "９月" },
  { value: "10", label: "１０月" },
  { value: "11", label: "１１月" },
  { value: "12", label: "１２月" },
  { value: "01", label: "１月" },
  { value: "02", label: "２月" },
  { value: "03", label: "３月" },
]

interface HygieneData {
  id?: string
  year: string
  month: string
  manager: string
  facilityChecks: Record<string, boolean>
  calibrationChecks: Record<string, boolean>
  inspectionChecks: Record<string, boolean>
  remarks: string
  verifier: string
  createdAt?: string
  updatedAt?: string
}

const HygieneManagement = () => {
  const theme = useTheme()
  const colors = tokens(theme.palette.mode)
  const navigate = useNavigate()
  const [allRecords, setAllRecords] = useState<HygieneData[]>([])
  const [holidays, setHolidays] = useState<IBusinessHoliday[]>([])
  const [selectedYear, setSelectedYear] = useState<string>(dayjs().format("YYYY"))
  const [availableYears, setAvailableYears] = useState<string[]>([])

  useEffect(() => {
    const hygieneManagementsRef = collection(db, "monthlyHygieneManagements")
    const unsubscribe = onSnapshot(hygieneManagementsRef, (snapshot: QuerySnapshot) => {
      const records = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as HygieneData))

      setAllRecords(records)

      // Extract unique years and sort them
      const years = [...new Set(records.map(r => r.year))].sort((a, b) => b.localeCompare(a))
      setAvailableYears(years)

      // If current year is not in the list, add it
      if (!years.includes(dayjs().format("YYYY"))) {
        setAvailableYears([dayjs().format("YYYY"), ...years])
      }
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "businessHolidays"), (snapshot) => {
      setHolidays(snapshot.docs.map(d => ({ id: d.id, ...d.data() } as IBusinessHoliday)))
    })
    return unsubscribe
  }, [])

  const getMonthLabel = (monthValue: string) => {
    const monthObj = MONTHS.find(m => m.value === monthValue)
    return monthObj ? monthObj.label : monthValue
  }

  const handleOpen = (year: string, month: string, recordId?: string) => {
    if (recordId) {
      navigate(`/admin/hygiene-management/${recordId}`)
    } else {
      navigate(`/admin/hygiene-management/new?year=${year}&month=${month}`)
    }
  }

  return (
    <Box m={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          衛生管理点検票（月次）
        </Typography>
        <Box display="flex" gap={2} alignItems="center">
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>年度</InputLabel>
            <Select
              value={selectedYear}
              label="年度"
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              {availableYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}年度
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: colors.blueAccent[700] }}>
              <TableCell sx={{ fontWeight: "bold" }}>対象月</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>状態</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>担当責任者</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>確認者</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>更新日</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {MONTHS.map((m) => {
              const record = allRecords.find(r => r.year === selectedYear && r.month === m.value)
              const hasData = !!record
              const firstDay = `${selectedYear}-${m.value}-01`
              const lastDay = dayjs(firstDay).endOf('month').format("YYYY-MM-DD")
              const isHoliday = !hasData && isRangeFullyHoliday(firstDay, lastDay, holidays)

              return (
                <TableRow key={m.value} hover>
                  <TableCell>{selectedYear}年{getMonthLabel(m.value)}</TableCell>
                  <TableCell>
                    {isHoliday ? (
                      <Chip label="休業" color="default" size="small" />
                    ) : hasData ? (
                      <Chip label="入力済" color="success" size="small" />
                    ) : (
                      <Chip label="未入力" color="warning" size="small" />
                    )}
                  </TableCell>
                  <TableCell>{record?.manager || "-"}</TableCell>
                  <TableCell>{record?.verifier || "-"}</TableCell>
                  <TableCell>{record?.updatedAt || "-"}</TableCell>
                  <TableCell>
                    {isHoliday ? (
                      <Typography variant="body2" color="textSecondary">
                        休業のため対象外
                      </Typography>
                    ) : hasData ? (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleOpen(selectedYear, m.value, record!.id)}
                      >
                        編集
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpen(selectedYear, m.value)}
                      >
                        入力
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}

export default HygieneManagement
